import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { anchorTo, Icon, TextInput, Tooltip, useAnchorRect } from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useI18n, useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C53 · which deliverable to plan somebody onto.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A MENU IS THE WRONG CONTROL ONCE THE LIST IS A WORKSPACE'S DELIVERABLES.** This was a
 * plain dropdown of every open line item: fine at the fixture's dozen, and a scroll through
 * several hundred in any workspace that has been running a year — with no way to narrow it, and no
 * way to tell two deliverables of the same name on different contracts apart.
 *
 * ⚠️ **So it searches, and every row says where it lives and what it is.** Project, contract,
 * estimate and window — the mockup's own four — which between them answer *is this the one* for
 * the two rows that would otherwise read identically.
 */

export interface PickableLineItem {
  readonly id: string;
  readonly name: string;
  /** 4 Nov 2026 · P-1 · whose members may be planned onto it — `LineItemPicker`'s `blocked`. */
  readonly projectId: string | null;
  readonly projectName: string | null;
  /** The contract's public identifier, as every screen names a contract (R-57). */
  readonly contractLabel: string | null;
  /**
   * 3 Nov 2026 · the contract's NAME — what the picker's caption reads (`contractOptions`); the
   * identifier above stays the address. Absent, the caption falls back to the identifier.
   */
  readonly contractName?: string | null;
  readonly estimateHours: string;
  readonly startsOn: string | null;
  readonly endsOn: string | null;
}

/** One row of a picker: what it is, and the caption that tells it apart from its namesakes. */
interface PickerRow {
  readonly id: string;
  readonly title: string;
  readonly caption: string;
  /** 4 Nov 2026 · why it cannot be picked; listed last, disabled, the reason on hover. */
  readonly blocked?: string | null;
  /**
   * 5 Nov 2026 · a blocked row that CAN be unblocked from here — the person added to the line
   * item's project. The row is then clickable (a lock and this hint) and opens that offer.
   */
  readonly unlock?: string | null;
}

/**
 * ⚠️ **The surface both pickers share** — the search, the capped list, the dismissal rules. What
 * differs is what a row says and what an empty list means, and both are the caller's.
 */
function PickerSurface({
  anchor,
  label,
  search,
  none,
  rows,
  onPick,
  onUnlock,
  onClose,
}: {
  readonly anchor: RefObject<HTMLElement | null>;
  readonly label: string;
  readonly search: string;
  readonly none: string;
  readonly rows: readonly PickerRow[];
  readonly onPick: (id: string) => void;
  /** 5 Nov 2026 · a blocked row with an `unlock` chosen — its offer, not a pick. */
  readonly onUnlock?: (id: string) => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  const { rect } = useAnchorRect(true, anchor);
  const surface = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    const onDown = (event: MouseEvent): void => {
      const node = event.target;
      if (!(node instanceof Node)) return;
      if (surface.current?.contains(node) === true) return;
      if (anchor.current?.contains(node) === true) return;
      /**
       * ⚠️⚠️ **A MENU OPENED FROM IN HERE IS NOT *OUTSIDE*, THOUGH THE DOM SAYS IT IS.**
       * `DropdownMenu` portals its surface into `document.body`, so it is nowhere near this one in
       * the tree — and `mousedown` runs BEFORE `click`. Without this, choosing an overtime type
       * closed the pop-over on the way down and the item's own handler never ran: every menu in
       * here silently did nothing. Found by a test, not by looking, because the pop-over shutting
       * is exactly what a reader expects to see after choosing something.
       */
      if (node instanceof Element && node.closest('[role="menu"]') !== null) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [anchor, onClose]);

  /**
   * ⚠️ **The caption is searched as well as the title**, because that is how somebody who knows
   * the commercial side looks for a deliverable — *cont_12a3cd5a* rather than *Havi támogatás* —
   * and it is the field the mockup's own placeholder names second.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched =
      needle === ''
        ? rows
        : rows.filter((row) => `${row.title} ${row.caption}`.toLowerCase().includes(needle));
    /* ⚠️ Listed, never hidden: a deliverable that vanished leaves *"where is it?"* unanswered —
       `Select`'s rule for an unpickable choice. Last, so the pickable ones come first. */
    return [
      ...matched.filter((row) => (row.blocked ?? null) === null),
      ...matched.filter((row) => (row.blocked ?? null) !== null),
    ];
  }, [rows, query]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={surface}
      role="dialog"
      aria-label={label}
      /* 5 Nov 2026 · 480, up from 340 — a caption names the project, the contract, the estimate
         and the window, and at 340 most of it was cut off. `anchorTo` still caps it to the screen. */
      className="bg-popover text-popover-foreground shadow-pop border-border w-[480px] max-w-[calc(100vw-16px)] overflow-hidden rounded-lg border"
      style={anchorTo(rect, { side: 'bottom', align: 'start', width: 480 })}
    >
      <div className="border-b p-2">
        <TextInput
          autoFocus
          value={query}
          placeholder={search}
          aria-label={search}
          className="h-8 text-[12px]"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
      </div>

      {/* ⚠️ Capped height and scrolled: a workspace's deliverables are not a list that fits. */}
      <div className="max-h-[280px] overflow-y-auto p-1">
        {shown.length === 0 ? (
          <p className="text-muted-foreground px-2 py-6 text-center text-[11.5px]">
            {query.trim() === '' ? none : t('resource.pick.noMatch')}
          </p>
        ) : (
          shown.map((row) => {
            const blocked = row.blocked ?? null;
            const unlock = blocked === null ? null : (row.unlock ?? null);
            if (unlock !== null) {
              /* 5 Nov 2026 · blocked, but this reader may lift it. The action sits on the title's
                 line, right-aligned, so the caption keeps its room — and no tooltip: the reason it
                 was blocked is moot when the fix is the click itself. */
              return (
                <button
                  key={row.id}
                  type="button"
                  className="hover:bg-accent flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors"
                  onClick={() => {
                    onUnlock?.(row.id);
                  }}
                >
                  <span className="flex w-full items-center gap-2">
                    <span className="text-muted-foreground min-w-0 flex-1 truncate text-[12.5px] font-medium">
                      {row.title}
                    </span>
                    <span className="text-primary flex shrink-0 items-center gap-1 text-[11px] font-medium">
                      <Icon name="plus" size={11} />
                      {unlock}
                    </span>
                  </span>
                  {row.caption === '' ? null : (
                    <span className="text-muted-foreground w-full truncate text-[10.5px]">
                      {row.caption}
                    </span>
                  )}
                </button>
              );
            }
            const button = (
              <button
                key={row.id}
                type="button"
                /* ⚠️ `aria-disabled`, not `disabled`: a disabled button takes no pointer events,
                   and the reason on hover is the whole point of listing it. */
                aria-disabled={blocked !== null}
                className={
                  blocked === null
                    ? 'hover:bg-accent flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors'
                    : 'flex w-full cursor-not-allowed flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left opacity-50'
                }
                onClick={() => {
                  if (blocked === null) onPick(row.id);
                }}
              >
                <span className="w-full truncate text-[12.5px] font-medium">{row.title}</span>
                {row.caption === '' ? null : (
                  <span className="text-muted-foreground w-full truncate text-[10.5px]">
                    {row.caption}
                  </span>
                )}
              </button>
            );
            return blocked === null ? (
              button
            ) : (
              <Tooltip key={row.id} content={blocked}>
                {button}
              </Tooltip>
            );
          })
        )}
      </div>
    </div>,
    document.body,
  );
}

export function LineItemPicker({
  anchor,
  items,
  blocked,
  unlock,
  onPick,
  onUnlock,
  onClose,
}: {
  readonly anchor: RefObject<HTMLElement | null>;
  readonly items: readonly PickableLineItem[];
  /** 4 Nov 2026 · why an item cannot be picked, or null — listed last and disabled. */
  readonly blocked?: (item: PickableLineItem) => string | null;
  /** 5 Nov 2026 · the hint on a blocked item this reader can unblock, or null. */
  readonly unlock?: (item: PickableLineItem) => string | null;
  readonly onUnlock?: (item: PickableLineItem) => void;
  readonly onPick: (item: PickableLineItem) => void;
  readonly onClose: () => void;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const { locale } = useI18n();

  const day = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }),
    [locale],
  );

  const window = (item: PickableLineItem): string => {
    if (item.startsOn === null && item.endsOn === null) return '';
    const from = item.startsOn === null ? '' : day.format(new Date(`${item.startsOn}T00:00:00Z`));
    const to = item.endsOn === null ? '' : day.format(new Date(`${item.endsOn}T00:00:00Z`));
    return `${from} – ${to}`;
  };

  return (
    <PickerSurface
      anchor={anchor}
      label={t('resource.item.add')}
      search={t('resource.pick.search')}
      none={t('resource.pick.none')}
      rows={items.map((item) => ({
        id: item.id,
        title: item.name,
        caption: [
          item.projectName,
          item.contractName ?? item.contractLabel,
          t('resource.pick.estimate', { hours: fmt(Number(item.estimateHours)) }),
          window(item),
        ]
          .filter((part) => part !== null && part !== '')
          .join(' · '),
        blocked: blocked?.(item) ?? null,
        unlock: unlock?.(item) ?? null,
      }))}
      onPick={(id) => {
        const item = items.find((one) => one.id === id);
        if (item !== undefined) onPick(item);
      }}
      onUnlock={(id) => {
        const item = items.find((one) => one.id === id);
        if (item !== undefined) onUnlock?.(item);
      }}
      onClose={onClose}
    />
  );
}

/** Somebody a line item could be given to — a member of its project. */
export interface PickablePerson {
  readonly id: string;
  readonly name: string;
  readonly roleName: string | null;
}

/**
 * ⚠️⚠️ **U-15 · THE OTHER DIRECTION: WHO TO PUT ON THIS DELIVERABLE.** The project tab's line-item
 * grid adds a PERSON under a line item where the people grid adds a line item under a person. The
 * caller passes only the project's members (P-1) — the server refuses anybody else.
 */
export function PersonPicker({
  anchor,
  people,
  onPick,
  onClose,
}: {
  readonly anchor: RefObject<HTMLElement | null>;
  readonly people: readonly PickablePerson[];
  readonly onPick: (person: PickablePerson) => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  return (
    <PickerSurface
      anchor={anchor}
      label={t('resource.person.add')}
      search={t('resource.pickPerson.search')}
      none={t('resource.pickPerson.none')}
      rows={people.map((person) => ({
        id: person.id,
        title: person.name,
        caption: person.roleName ?? '',
      }))}
      onPick={(id) => {
        const person = people.find((one) => one.id === id);
        if (person !== undefined) onPick(person);
      }}
      onClose={onClose}
    />
  );
}

/** The trigger, so the grid does not have to know how the picker is opened. */
export function PickerTrigger({
  label,
  onOpen,
}: {
  readonly label: string;
  readonly onOpen: (element: HTMLElement) => void;
}) {
  return (
    <button
      type="button"
      className="text-muted-foreground hover:bg-accent hover:text-foreground flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] transition-colors"
      onClick={(event) => {
        onOpen(event.currentTarget);
      }}
    >
      <Icon name="plus" size={13} />
      {label}
    </button>
  );
}
